import { familyService } from './familyService';
import { db } from '../db/indexedDB';
import type { Family, Member, Relationship } from '../types';

const PUSH_DEBOUNCE_MS = 1500;
const pushTimers = new Map<string, ReturnType<typeof setTimeout>>();
let isPulling = false;

function apiBase(): string {
  return '/api';
}

async function upsertFamily(family: Family): Promise<void> {
  const existing = await db.getById<Family>('families', family.id);
  if (existing) await db.update('families', family);
  else await db.add('families', family);
}

async function upsertMember(member: Member): Promise<void> {
  const existing = await db.getById<Member>('members', member.id);
  if (existing) await db.update('members', member);
  else await db.add('members', member);
}

async function upsertRelationship(rel: Relationship): Promise<void> {
  const existing = await db.getById<Relationship>('relationships', rel.id);
  if (existing) await db.update('relationships', rel);
  else await db.add('relationships', rel);
}

export const neonService = {
  /** Debounced background push — UI block nahi hota */
  schedulePush(familyId: string | undefined | null) {
    if (!familyId || isPulling) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    const prev = pushTimers.get(familyId);
    if (prev) clearTimeout(prev);

    const t = setTimeout(() => {
      pushTimers.delete(familyId);
      void this.pushFamily(familyId);
    }, PUSH_DEBOUNCE_MS);

    pushTimers.set(familyId, t);
  },

    /** Local IndexedDB → Neon (full family snapshot) */
  async pushFamily(familyId: string): Promise<boolean> {
    try {
      const family = await familyService.getFamily(familyId);
      if (!family) return false;
      // Local demo / anonymous — never touch Neon
      if (!family.userId || family.userId === 'default') {
        console.log('%c☁️ Skip cloud push (local demo/default family)', 'color:#9E9E9E;');
        return false;
      }

      const members = await familyService.getMembersByFamily(familyId);
      const relationships = await familyService.getRelationshipsByFamily(familyId);

      const res = await fetch(`${apiBase()}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ family, members, relationships }),
      });

      // 🔐 Auth & Ownership Guards
      if (res.status === 401) {
        alert('Login required to sync your tree.');
        window.dispatchEvent(new CustomEvent('tiafa:auth-required'));
        throw new Error('Login required');
      }
      if (res.status === 403) {
        alert('You are not the owner of this family tree.');
        throw new Error('Forbidden');
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[neon] push failed:', err?.error || res.status);
        return false;
      }

      const data = await res.json();
      console.log(
        `%c☁️ Cloud sync OK — ${data?.counts?.members ?? members.length} members`,
        'color:#2196F3;font-weight:bold;'
      );
      return true;
    } catch (e) {
      console.warn('[neon] push error (offline/ok to ignore):', e);
      return false;
    }
  },

  /**
   * Neon → IndexedDB merge with Timestamp Conflict Resolution
   */
  async pullFamily(familyId: string): Promise<boolean> {
    if (!familyId) return false;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return false;

    isPulling = true;
    try {
      const res = await fetch(
        `${apiBase()}/sync?familyId=${encodeURIComponent(familyId)}`
      );

      if (res.status === 404) return false;

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[neon] pull failed:', err?.error || res.status);
        return false;
      }

      const data = await res.json();
      if (!data?.success || !data.family) return false;

      const cloudFamily = data.family as Family;
      const members = (data.members || []) as Member[];
      const relationships = (data.relationships || []) as Relationship[];

      // ⏱️ CONFLICT RESOLUTION BY TIMESTAMP
      const localFamily = await familyService.getFamily(familyId);
      if (localFamily && localFamily.updatedAt > (cloudFamily.updatedAt || 0)) {
        console.log(
          `%c☁️ Local tree is NEWER than cloud. Triggering push to sync cloud...`,
          'color:#FF9800;font-weight:bold;'
        );
        isPulling = false;
        void this.pushFamily(familyId);
        return true;
      }

      // Cloud is newer or equal -> Upsert to local IndexedDB
      await upsertFamily({
        id: cloudFamily.id,
        userId: cloudFamily.userId || 'default',
        name: cloudFamily.name,
        createdAt: cloudFamily.createdAt || Date.now(),
        updatedAt: cloudFamily.updatedAt || Date.now(),
      });

      for (const m of members) {
        await upsertMember({
          ...m,
          familyId: cloudFamily.id,
          isDeceased: !!m.isDeceased,
          createdAt: m.createdAt || Date.now(),
          updatedAt: m.updatedAt || Date.now(),
        });
      }

      for (const r of relationships) {
        await upsertRelationship({
          ...r,
          familyId: cloudFamily.id,
          metadata: r.metadata || undefined,
        });
      }

      console.log(
        `%c☁️ Pulled from cloud — ${members.length} members, ${relationships.length} rels`,
        'color:#4CAF50;font-weight:bold;'
      );
      return true;
    } catch (e) {
      console.warn('[neon] pull error (offline/ok to ignore):', e);
      return false;
    } finally {
      isPulling = false;
    }
  },

    /** Delete family from Cloud */
    async deleteFamily(familyId: string): Promise<boolean> {
    if (!familyId) return false;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return false;

    try {
      // optional: agar body me user check backend pe hai to 403/401 aa sakta hai — demo call avoid best
      const res = await fetch(
        `${apiBase()}/sync?familyId=${encodeURIComponent(familyId)}`,
        { method: 'DELETE' }
      );

      // 🔐 Auth & Ownership Guards
      if (res.status === 401) {
        alert('Login required to delete this tree.');
        window.dispatchEvent(new CustomEvent('tiafa:auth-required'));
        throw new Error('Login required');
      }
      if (res.status === 403) {
        alert('You are not the owner of this family tree.');
        throw new Error('Forbidden');
      }

      if (!res.ok) return false;
      console.log(
        `%c☁️ Cloud family deleted — ${familyId}`,
        'color:#FF5722;font-weight:bold;'
      );
      return true;
    } catch (e) {
      console.warn('[neon] delete error:', e);
      return false;
    }
  },
};